import { token } from '@atlaskit/tokens';

import { HUB_PRIMARY_COLOR_CSS_VAR, HUB_PRIMARY_TEXT_COLOR_CSS_VAR } from '../../constants';
import { HUB_COLOR_CSS_VARS } from '../../utils/hub-brand-palette';
import type { HubBrandPalette, PaletteRef } from '../../utils/hub-brand-palette';

export type HubDefaultParameters = {
	backgroundColor: string;
	textColor: string;
};

/**
 * Day-0 default shade that bespoke extensions inherit through
 * `CommonHubExtensionsDependenciesProvider` when an extension hasn't pinned a
 * background of its own. Kept local to this resolver — it is intentionally
 * decoupled from `ELEMENT_DEFAULTS` because the per-element map is the
 * source-of-truth for *named* element slots (e.g. `callout.bg`), whereas this
 * is the resolver's own generic fallback shared across every extension.
 */
const DEFAULT_HUB_BACKGROUND_SHADE: PaletteRef = 'primary.200';

/**
 * Optional inputs that opt the default-parameters resolution into the
 * advanced-theming palette path. When `palette` is supplied, the bespoke-
 * element background defaults to the brand `primary.200` shade resolved from
 * that palette (per §4.2 of the Advanced Theming v2 design). When `palette`
 * is absent, behaviour is identical to the legacy single-brand-color path.
 */
export type HubDefaultParametersOptions = {
	palette?: HubBrandPalette;
};

export const getHubDefaultParameters = (
	options: HubDefaultParametersOptions = {},
): HubDefaultParameters => {
	if (options.palette) {
		// Gate ON: return a CSS-var reference (not the hex literal) so the
		// <HubThemingProvider> wrapper drives all repaints via the cascade.
		// `palette` is only used as a structural opt-in marker here — the
		// var resolves against the wrapper's inline `style` at paint time.
		return {
			backgroundColor: `var(${HUB_COLOR_CSS_VARS[DEFAULT_HUB_BACKGROUND_SHADE]})`,
			textColor: `var(${HUB_PRIMARY_TEXT_COLOR_CSS_VAR}, ${token('color.text')})`,
		};
	}

	return {
		backgroundColor: `var(${HUB_PRIMARY_COLOR_CSS_VAR}, ${token('color.background.accent.gray.subtler')})`,
		textColor: `var(${HUB_PRIMARY_TEXT_COLOR_CSS_VAR}, ${token('color.text')})`,
	};
};
